import React, { useRef, useState } from "react";
import _ from "underscore";

type TArrayInputProps<T> = {
    height?: number;
    defaultSize?: number;
    limit?: number;
    disabled?: boolean;
    /** Workaround specifically for TrackPane, is there a better way? */
    noValueChangeCheck?: boolean;
    onUpdate: (val: T | T[]) => void;
    defaultValue: {
        label: string;
        value: T;
        selected: boolean;
        disabled?: boolean;
        /** Unused here; OrderEditor carries an id on its option literals. */
        id?: number;
    }[];
};

const getSelectedIndexes = <T,>(props: TArrayInputProps<T>) => {
    const selection = props.defaultValue.reduce((indexes: number[], item, index: number) => {
        if (item.selected) {
            return indexes.concat(index);
        }
        return indexes;
    }, []);

    if (props.limit === 1) {
        return selection[0];
    }

    return selection;
};

// Generic components cannot use the TComponent alias, so the props are annotated
// directly. The trailing comma in <T,> keeps TSX from reading it as a JSX tag.
const ArrayInput = <T,>(props: TArrayInputProps<T>) => {
    const optionRefs = useRef<(HTMLOptionElement | null)[]>([]);
    const [value, setValue] = useState<number | number[]>(() => getSelectedIndexes(props));

    // Replaces the old componentDidUpdate, which re-synced whenever the selection
    // derived from props diverged from local state.
    if (!props.noValueChangeCheck) {
        const indexes = getSelectedIndexes(props);
        if (!_.isEqual(indexes, value)) {
            setValue(indexes);
        }
    }

    const onChange = () => {
        const vals: T[] = [];
        const indexes: number[] = [];
        props.defaultValue.forEach((item, index) => {
            if (optionRefs.current[index]?.selected) {
                indexes.push(index);
                vals.push(item.value);
            }
        });

        if (props.limit === 1) {
            setValue(indexes[0]);
            props.onUpdate(vals[0]);
        } else {
            setValue(indexes);
            props.onUpdate(vals);
        }
    };

    let size = props.defaultSize || 0;
    if (props.limit === 1) {
        size = 1;
    }

    let style: React.CSSProperties = {};

    if (props.height) {
        style = {
            height: props.height,
        };
    }

    return (
        <select
            disabled={props.disabled || false}
            style={style}
            multiple={props.limit !== 1}
            onChange={onChange}
            // React's typing for select.value omits readonly number[], though it
            // handles numbers fine at runtime (it stringifies them).
            value={value as string | number | readonly string[]}
            size={size}
        >
            {props.defaultValue.map((item, index) => (
                <option
                    key={index}
                    disabled={item.disabled ? true : undefined}
                    ref={(el) => {
                        optionRefs.current[index] = el;
                    }}
                    value={index}
                >
                    {item.label}
                </option>
            ))}
        </select>
    );
};

export default ArrayInput;
